import { Alert, Box, Link, Typography } from "@mui/material";

export type CmsAdminDebugSnapshot =
  | {
      requestUrl: string;
      outcome: "success";
      responseBody: unknown;
    }
  | {
      requestUrl: string;
      outcome: "error";
      status: number;
      message: string;
      error_code?: string;
      detail?: unknown;
    };

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

const preSx = {
  m: 0,
  mb: 0,
  p: 1,
  borderRadius: 1,
  fontSize: "11px",
  overflow: "auto" as const,
};

/**
 * Request URL + response or error payload for global administrators debugging CMS API calls.
 */
export function CmsLookupAdminDebugPanel({
  title,
  snapshot,
}: {
  title: string;
  snapshot: CmsAdminDebugSnapshot | null;
}) {
  if (!snapshot) {
    return null;
  }

  return (
    <Alert severity="info" sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 600, display: "block", color: "text.secondary" }}>
        Request
      </Typography>
      <Box
        component="pre"
        sx={{
          ...preSx,
          mb: 1.5,
          bgcolor: "#f8fafc",
          maxHeight: 140,
        }}
      >
        GET {snapshot.requestUrl}
      </Box>

      {snapshot.outcome === "success" ? (
        <>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", color: "text.secondary" }}>
            Response body
          </Typography>
          <Box
            component="pre"
            sx={{
              ...preSx,
              bgcolor: "#f0fdfa",
              maxHeight: 320,
            }}
          >
            {formatJson(snapshot.responseBody)}
          </Box>
        </>
      ) : (
        <>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", color: "text.secondary" }}>
            Error
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "12px", mb: 0.5 }}>
            HTTP {snapshot.status}
            {snapshot.error_code ? ` · ${snapshot.error_code}` : ""}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, fontSize: "12px" }}>
            {snapshot.message}
          </Typography>
          {snapshot.requestUrl ? (
            <Typography variant="caption" display="block" sx={{ mb: snapshot.detail != null ? 1 : 0 }}>
              <Link href={snapshot.requestUrl} target="_blank" rel="noopener noreferrer">
                Open request URL
              </Link>
            </Typography>
          ) : null}
          {snapshot.detail != null ? (
            <Box
              component="pre"
              sx={{
                ...preSx,
                bgcolor: "#fef2f2",
                maxHeight: 240,
              }}
            >
              {typeof snapshot.detail === "string"
                ? snapshot.detail
                : formatJson(snapshot.detail)}
            </Box>
          ) : null}
        </>
      )}
    </Alert>
  );
}
